import { useEffect, useRef } from 'react';
import './cursor-trail.css';

type CursorTrailProps = {
  enabled: boolean;
};

const PARTICLE_COUNT = 12;

export default function CursorTrail({ enabled }: CursorTrailProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!finePointer.matches || reducedMotion.matches) return;

    const container = containerRef.current;
    if (!container) return;

    const particles = Array.from(
      container.querySelectorAll<HTMLElement>('.cursor-trail__particle'),
    );

    const positions = particles.map(() => ({
      x: -100,
      y: -100,
    }));

    let mouseX = -100;
    let mouseY = -100;
    let animationFrame = 0;
    let visible = false;

    const handleMouseMove = (event: MouseEvent) => {
      mouseX = event.clientX;
      mouseY = event.clientY;

      if (!visible) {
        visible = true;
        container.classList.add('is-visible');

        positions.forEach((position) => {
          position.x = mouseX;
          position.y = mouseY;
        });
      }

      if (!animationFrame) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    const animate = () => {
      positions.forEach((position, index) => {
        const previous =
          index === 0 ? { x: mouseX, y: mouseY } : positions[index - 1];

        const easing = index === 0 ? 0.42 : 0.38;

        position.x += (previous.x - position.x) * easing;
        position.y += (previous.y - position.y) * easing;

        const particle = particles[index];

        particle.style.transform = `translate3d(${position.x}px, ${position.y}px, 0) translate(-50%, -50%)`;
      });

      const last = positions[positions.length - 1];
      const distance = Math.hypot(mouseX - last.x, mouseY - last.y);

      if (distance > 0.15) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        animationFrame = 0;
      }
    };

    window.addEventListener('mousemove', handleMouseMove, {
      passive: true,
    });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div ref={containerRef} className="cursor-trail" aria-hidden="true">
      {Array.from({ length: PARTICLE_COUNT }, (_, index) => (
        <span
          key={index}
          className="cursor-trail__particle"
          style={
            {
              '--particle-index': index,
              '--particle-scale': 1 - index / PARTICLE_COUNT,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
